import { FC, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import { BottomSheet } from '@beeline/design-system-react/extracted-styles';
import { BottomSheetActionsType } from '@beeline/design-system-react/types/components/BottomSheet/BottomSheet.types';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import {
  MyBookingsPageRoleProvider,
  useMyBookingsPageRole,
} from 'src/pages/MyBookingsPage/MyBookingsPageRole/MyBookingsPageRoleProvider';
import { useHost } from 'src/providers/HostProvider';
import { getBookingsState } from 'src/store/bookings/selectors';
import { bookingsActions } from 'src/store/bookings/slice';
import { BookingsPageRole } from 'src/store/bookings/types';
import { useAppDispatch } from 'src/store/useAppDispatch';

import { MyBookingsFiltersFields } from '../MyBookingsFiltersFields';
import { MyBookingsFiltersForm } from '../MyBookingsFiltersForm';

import classes from './FiltersBottomSheet.module.scss';

const Fields: FC = () => {
  const dispatch = useAppDispatch();
  const { setRole } = useMyBookingsPageRole();

  const { submit, reset } = useForm();

  const { filtersAreOpened } = useSelector(getBookingsState);

  const close = useCallback(() => {
    dispatch(bookingsActions.closeFilters());
  }, [dispatch]);

  const applyFilters = useCallback(async () => {
    await submit();
    close();
    reset();
  }, [close, reset, submit]);

  const resetFilters = useCallback(async () => {
    dispatch(bookingsActions.resetFilters());
    setRole(BookingsPageRole.Responsible);

    close();
    reset();
  }, [close, dispatch, reset, setRole]);

  const actions = useMemo((): BottomSheetActionsType | undefined => {
    const result: BottomSheetActionsType = [
      {
        label: 'Сбросить',
        variant: 'outlined',
        onClick: resetFilters,
      },
      {
        label: 'Применить',
        variant: 'contained',
        onClick: applyFilters,
      },
    ];

    return result;
  }, [applyFilters, resetFilters]);

  const handleSetOpen = useCallback(
    (value: boolean) => {
      dispatch(bookingsActions.setFiltersAreOpened(value));
      reset();
    },
    [dispatch, reset],
  );

  return (
    <BottomSheet
      isOpen={filtersAreOpened}
      handleSetOpen={handleSetOpen}
      swiper
      modal={false}
      snapPoints={[1]}
      className={classes.settings}
      initialSnapPoint={0}
      header={{
        icon: Icons.Filter,
        title: 'Фильтры',
        closeButton: true,
      }}
      actions={actions}
    >
      <div className={classes.container}>
        <MyBookingsFiltersFields />
      </div>
    </BottomSheet>
  );
};

const FormBottomSheet: FC = () => {
  return (
    <MyBookingsPageRoleProvider>
      <MyBookingsFiltersForm>
        <Fields />
      </MyBookingsFiltersForm>
    </MyBookingsPageRoleProvider>
  );
};

export const FiltersBottomSheet: FC = () => {
  const { host } = useHost();

  if (!host) return null;

  return createPortal(<FormBottomSheet />, host);
};
